import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  apiClient,
  publicApi,
  type CurrencyRef,
  type MasterDataItem,
  type MasterDataRef,
  type RegionItem,
} from '../lib/apiClient';
import { orderRegions } from '../lib/regionOrder';

/**
 * Phase 7-avatar: in-memory cache for master-data lookups (regions / industries / job
 * categories / employment types / work arrangements / experience levels / company sizes).
 *
 * Phase 7-dashboard-b extension: also caches `noticePeriods`, `currencies`, `payPeriods`
 * for the candidate dashboard's My Profile (job preferences card) without adding extra
 * fetch traffic on every render.
 *
 * Why a context cache?
 *   - The data is essentially static — Admin App master-data CRUD won't ship until Phase 8.
 *   - Without it, every navigation to `/job-seekers`, `/signup`, or the hiring intake
 *     modal re-fetches all eight dropdowns. React 18 StrictMode dev doubles every fetch.
 *     During Phase 6.1 verification, that compound traffic was tripping the public 100/min
 *     throttle bucket on a single browse session.
 *   - Backend now uses a separate `master_data_public` 1000/min bucket; the frontend cache
 *     keeps that bucket effectively untouched in normal use, leaving headroom for crawlers
 *     and bot probes.
 *
 * Cities are not cached at all — they change frequently as admins add/edit/disable cities,
 * and a stale per-region cache that survives page reloads (the JS bundle is cached by the
 * browser) prevents users from seeing newly-added cities without a hard refresh. Callers
 * request them on demand via `loadCities(regionSlug)` and every call hits `/public/cities`.
 * The payload per region is small (~10-30 items) so the network cost is negligible.
 *
 * No persistence: this is a session-scoped cache. A full page reload re-fetches.
 */
export interface MasterData {
  regions: RegionItem[];
  industries: MasterDataItem[];
  jobCategories: MasterDataItem[];
  experienceLevels: MasterDataItem[];
  employmentTypes: MasterDataItem[];
  workArrangements: MasterDataItem[];
  companySizes: MasterDataItem[];
  /** Phase 7-dashboard-b — Job Preferences card. */
  noticePeriods: MasterDataRef[];
  /** Phase 7-dashboard-b — Expected salary currency dropdown. */
  currencies: CurrencyRef[];
  /** Phase 7-dashboard-b — Expected salary pay-period dropdown. */
  payPeriods: MasterDataRef[];
}

export type MasterDataStatus = 'idle' | 'loading' | 'ready' | 'error';

interface MasterDataContextValue {
  status: MasterDataStatus;
  data: MasterData | null;
  errorMessage: string | null;
  /** Force a re-fetch (e.g., from a "Couldn't load filters" banner's Retry button). */
  refresh: () => Promise<void>;
  /** Load a region's cities; cached per region for the session. */
  loadCities: (regionSlug: string) => Promise<MasterDataItem[]>;
}

const MasterDataContext = createContext<MasterDataContextValue | null>(null);

const EMPTY_DATA: MasterData = {
  regions: [],
  industries: [],
  jobCategories: [],
  experienceLevels: [],
  employmentTypes: [],
  workArrangements: [],
  companySizes: [],
  noticePeriods: [],
  currencies: [],
  payPeriods: [],
};

export function MasterDataProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<MasterDataStatus>('idle');
  const [data, setData] = useState<MasterData | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  /** StrictMode dev fires effects twice; this guards against the double initial fetch. */
  const fetchedOnce = useRef(false);

  const fetchAll = useCallback(async () => {
    setStatus('loading');
    setErrorMessage(null);
    try {
      const [
        regions,
        industries,
        jobCategories,
        experienceLevels,
        employmentTypes,
        workArrangements,
        companySizes,
        noticePeriods,
        currencies,
        payPeriods,
      ] = await Promise.all([
        publicApi.regions(),
        publicApi.industries(),
        publicApi.jobCategories(),
        publicApi.experienceLevels(),
        publicApi.employmentTypes(),
        publicApi.workArrangements(),
        publicApi.companySizes(),
        apiClient.get<MasterDataRef[]>('/public/notice-periods'),
        apiClient.get<CurrencyRef[]>('/public/currencies'),
        apiClient.get<MasterDataRef[]>('/public/pay-periods'),
      ]);
      setData({
        regions: orderRegions(regions),
        industries,
        jobCategories,
        experienceLevels,
        employmentTypes,
        workArrangements,
        companySizes,
        noticePeriods,
        currencies,
        payPeriods,
      });
      setStatus('ready');
    } catch {
      setData(EMPTY_DATA);
      setStatus('error');
      setErrorMessage("Couldn't load filters — please refresh the page.");
    }
  }, []);

  useEffect(() => {
    if (fetchedOnce.current) return;
    fetchedOnce.current = true;
    void fetchAll();
  }, [fetchAll]);

  const loadCities = useCallback(
    (regionSlug: string): Promise<MasterDataItem[]> => publicApi.cities(regionSlug),
    [],
  );

  const value = useMemo<MasterDataContextValue>(
    () => ({
      status,
      data,
      errorMessage,
      refresh: fetchAll,
      loadCities,
    }),
    [status, data, errorMessage, fetchAll, loadCities],
  );

  return <MasterDataContext.Provider value={value}>{children}</MasterDataContext.Provider>;
}

export function useMasterData(): MasterDataContextValue {
  const ctx = useContext(MasterDataContext);
  if (!ctx) throw new Error('useMasterData must be used within <MasterDataProvider>');
  return ctx;
}
